import React from "react";
import { KeyTerm, ImportantNote } from "../note-components";
import type { UnitNotes } from "../types";

const governmentUnit4Notes: UnitNotes = {
  unitId: "unit-4",
  unitName: "Unit 4: Party and Electoral Systems and Citizen Organizations",
  examWeight: "20–25%",
  focus: "How electoral rules, party systems, and citizen organizations shape political competition and representation.",
  sections: [
    {
      id: "topic-4-1",
      title: "Topic 4.1: Electoral Systems and Rules",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🗳️ Key Electoral Concepts</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="Single-Member District (SMD) / First-Past-The-Post (FPTP) / Plurality" 
                definition="Country divided into districts; one person elected per district. Candidate with most votes (plurality) wins. Generally encourages two-party system (Duverger's Law)."
              />
              <KeyTerm 
                term="Proportional Representation (PR)" 
                definition="Voters vote for party; seats awarded based on percentage of vote. Encourages multi-party system and coalition governments."
              />
              <KeyTerm 
                term="Mixed/Hybrid System" 
                definition="Combines SMD and PR. Voters usually cast two ballots (one for candidate, one for party)."
              />
              <KeyTerm 
                term="Majority Run-off (Two-Round System)" 
                definition="If no candidate wins majority (50%+1) in first round, second election held between top two candidates."
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Applications</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Executive Election:</strong> N/A (PM chosen by majority party in House of Commons)</p>
                <p className="text-sm text-slate-700"><strong>Legislative:</strong> SMD/FPTP for House of Commons (650 seats)</p>
                <p className="text-xs text-blue-600 mt-2">Effect: Encourages two-party dominance (Conservative vs. Labour). Small parties underrepresented. Regional parties (SNP) fare well due to geographic concentration.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Executive Election:</strong> Majority Run-off (if no majority, top two go to round 2)</p>
                <p className="text-sm text-slate-700"><strong>Legislative:</strong> Mixed System for State Duma (450 seats). 225 via SMD; 225 via PR (5% threshold)</p>
                <p className="text-xs text-orange-600 mt-2">Note: Switch from Mixed to PR (2007) and back to Mixed (2016) engineered to secure dominance for United Russia party.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Executive Election:</strong> N/A (President selected by National People's Congress)</p>
                <p className="text-sm text-slate-700"><strong>Legislative:</strong> Indirect Elections. Citizens vote for local congresses; local vote for regional; regional vote for NPC</p>
                <p className="text-xs text-red-600 mt-2">Not democratic. Elections are mechanisms for CCP to vet candidates and maintain legitimacy/order.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Executive Election:</strong> Plurality (FPTP). Winner needs most votes, not majority. No run-off. 1 term limit (Sexenio)</p>
                <p className="text-sm text-slate-700"><strong>Legislative:</strong> Mixed System. Chamber of Deputies: 300 SMD, 200 PR. Senate: Mixed formula</p>
                <p className="text-xs text-green-600 mt-2">Gender Quotas: Law requires 50% of candidates to be women, leading to high female representation.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Executive Election:</strong> Modified Two-Round. Winner must get majority AND 25% of vote in 2/3 of states</p>
                <p className="text-sm text-slate-700"><strong>Legislative:</strong> SMD/FPTP for House and Senate</p>
                <p className="text-xs text-purple-600 mt-2">The "25% in 2/3 states" rule is Federal Character Principle - ensures President has broad, national support (crossing ethnic/religious lines).</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Executive Election:</strong> Majority Run-off. Top two advance if no majority</p>
                <p className="text-sm text-slate-700"><strong>Legislative:</strong> SMD/Multimember Districts for Majles. Two rounds if candidates don't meet threshold</p>
                <p className="text-xs text-green-600 mt-2">Guardian Council Vetting: All candidates must be approved, excluding most reformists. 5 seats reserved for religious minorities.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-2",
      title: "Topic 4.2: Objectives of Election Rules",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎯 Objectives of Different Systems</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">SMD/Plurality Objectives</h4>
                <p className="text-sm text-slate-700 mt-2">✓ Create stability through two major parties</p>
                <p className="text-sm text-slate-700">✓ Foster strong link between representative and geographic constituency (Constituency Service)</p>
                <p className="text-xs text-red-600 mt-2">Risk: "Manufactured majority" - party wins legislative control with less than 50% of popular vote</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">PR Objectives</h4>
                <p className="text-sm text-slate-700 mt-2">✓ Ensure legislature accurately reflects political divisions in society</p>
                <p className="text-sm text-slate-700">✓ Allow minority voices and smaller parties to participate</p>
                <p className="text-xs text-red-600 mt-2">Risk: Can lead to unstable coalitions or legislative gridlock</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Electoral Regulations and Oversight</h3>
            <p className="text-sm text-slate-700 mb-4">The independence of election commissions is a key indicator of democratization.</p>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Independent Election Commissions (Democratic/Transitional)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>🇲🇽 Mexico (INE):</strong> Highly respected, independent. Regulates campaign finance, ensures fair vote counting. Created to end PRI fraud.</p>
                <p className="text-sm text-slate-700"><strong>🇳🇬 Nigeria (INEC):</strong> Established to reduce fraud. Improved (biometric readers) but still faces challenges with corruption, logistics, violence.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">State-Controlled Oversight (Authoritarian)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>🇮🇷 Iran (Guardian Council):</strong> Unelected body of 12 jurists/theologians. Disqualifies thousands of candidates (mostly reformists), drastically limiting genuine competition.</p>
                <p className="text-sm text-slate-700"><strong>🇷🇺 Russia (Central Election Commission):</strong> Theoretically independent but effectively controlled by Kremlin. Creates registration hurdles to bar opposition candidates (e.g., Navalny).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👥 Appointment Systems</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom (House of Lords)</h4>
                <p className="text-sm text-slate-700 mt-2">Appointed (Life Peers) or hereditary. Acts as revision chamber; limits "mob rule" but lacks democratic legitimacy.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia (Federation Council)</h4>
                <p className="text-sm text-slate-700 mt-2">Representatives appointed by regional governors and legislatures. Used by Putin to re-centralize power and ensure regions remain loyal to Kremlin.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-3",
      title: "Topic 4.3: Political Party Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎭 Types of Party Systems</h3>
            <div className="space-y-3">
              <KeyTerm 
                term="One-Party System" 
                definition="Only one party has legal right to rule. Example: China"
              />
              <KeyTerm 
                term="Dominant Party System" 
                definition="Multiple parties exist and compete, but one party wins every election and monopolizes power. Examples: Russia; Mexico under PRI"
              />
              <KeyTerm 
                term="Two-Party System" 
                definition="Two major parties dominate, though others exist. Examples: UK, Nigeria slightly"
              />
              <KeyTerm 
                term="Multi-Party System" 
                definition="No single party controls legislature; coalitions necessary. Example: Mexico currently"
              />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Applications</h3>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China (One-Party)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Chinese Communist Party (CCP):</strong> The "vanguard" party. Controls state, military, media.</p>
                <p className="text-sm text-slate-700"><strong>8 minor "democratic parties":</strong> Exist only for consultation and subservience to CCP.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia (Dominant Party)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>United Russia:</strong> "Party of Power." No strong ideology other than supporting Putin.</p>
                <p className="text-sm text-slate-700"><strong>Communist Party (KPRF):</strong> Main opposition but marginalized.</p>
                <p className="text-sm text-slate-700"><strong>LDPR:</strong> Nationalist/populist.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇲🇽 Mexico (Multi-Party)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>PRI:</strong> Centrist/Catch-all, ruled for 71 years (patronage/clientelism)</p>
                <p className="text-sm text-slate-700"><strong>PAN:</strong> Right-wing, Catholic, pro-business (Northern support)</p>
                <p className="text-sm text-slate-700"><strong>PRD:</strong> Left-wing</p>
                <p className="text-sm text-slate-700"><strong>MORENA:</strong> Current ruling party (populist/leftist under AMLO)</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇳🇬 Nigeria (Multi-Party)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>APC & PDP:</strong> Two major parties. Personality-driven rather than ideological.</p>
                <p className="text-sm text-slate-700"><strong>Zoning:</strong> Rotating candidates between North/South to maintain stability.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom (Two-Party)</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>Conservative (Tories):</strong> Center-right, austerity, Brexit</p>
                <p className="text-sm text-slate-700"><strong>Labour:</strong> Center-left, unions, social services</p>
                <p className="text-sm text-slate-700"><strong>SNP:</strong> Scottish independence</p>
                <p className="text-sm text-slate-700"><strong>Lib Dems:</strong> Centrist</p>
                <p className="text-xs text-blue-600 mt-2">High party discipline (MPs vote with leadership)</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran (No Formal Parties)</h4>
                <p className="text-sm text-slate-700 mt-2">Parties are weak/banned. Politics operate through loose factions:</p>
                <p className="text-sm text-slate-700"><strong>Conservatives/Hardliners:</strong> Support theocracy/Supreme Leader</p>
                <p className="text-sm text-slate-700"><strong>Reformists:</strong> Support democratization/engagement with West</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-4",
      title: "Topic 4.4: Role of Political Party Systems",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔗 Parties as Linkage Institutions</h3>
            <p className="text-sm text-slate-700 mb-4">Parties link citizens to government. Their roles vary based on level of competition.</p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🎯 Catch-All Parties</h3>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">Parties that attempt to appeal to widest range of voters by having vague or inclusive ideology.</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Examples:</strong> PRI in Mexico (historically), United Russia, PDP in Nigeria</p>
              <p className="text-sm text-slate-700 mt-2"><strong>Goal:</strong> Gain majority and maintain stability, often diluting specific policy goals.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">👤 Personality Cults vs. Ideology</h3>
            <div className="space-y-3">
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇷🇺 Russia</h4>
                <p className="text-sm text-slate-700 mt-2">United Russia centers almost entirely on Putin's personality and stability, rather than distinct economic or social platform.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇨🇳 China</h4>
                <p className="text-sm text-slate-700 mt-2">CCP is strictly ideological (Socialism with Chinese Characteristics), though it adapts pragmatically (economic liberalization).</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom</h4>
                <p className="text-sm text-slate-700 mt-2">Parties are highly ideological and disciplined (Party Whips ensure MPs vote the party line).</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">💰 Clientelism and Patronage</h3>
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-4">
              <h4 className="font-bold text-purple-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">Improving election chances by trading specific benefits (jobs, money, food) to individuals in exchange for political support.</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇲🇽 Mexico:</strong> PRI maintained dominance for decades through camarillas (patron-client networks).</p>
              <p className="text-sm text-slate-700"><strong>🇳🇬 Nigeria:</strong> "Prebendalism" - using government offices to secure wealth for one's ethnic group or clients.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-5",
      title: "Topic 4.5: Impact of Social Movements and Interest Groups",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🔀 Social Movements vs. Interest Groups</h3>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">Social Movements</h4>
                <p className="text-sm text-slate-700 mt-2">Large, often informal, loose groups demanding broad social or political change.</p>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Interest Groups</h4>
                <p className="text-sm text-slate-700 mt-2">Organized groups advocating for a specific policy issue.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🌍 Country Examples of Social Movements</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">🇮🇷 Iran (Green Movement, 2009)</h4>
                <p className="text-sm text-slate-700 mt-2">Massive uprising demanding removal of Ahmadinejad after fraudulent election. "Where is my vote?" Violently suppressed by Basij/Revolutionary Guard.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">🇲🇽 Mexico (Zapatistas/EZLN)</h4>
                <p className="text-sm text-slate-700 mt-2">Indigenous movement in South (Chiapas) demanding land rights and opposing NAFTA/neoliberalism. Forced government to acknowledge indigenous rights.</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">🇳🇬 Nigeria</h4>
                <p className="text-sm text-slate-700 mt-2"><strong>MEND:</strong> Militant group in Niger Delta demanding share of oil profits and environmental cleanup.</p>
                <p className="text-sm text-slate-700"><strong>Boko Haram:</strong> Islamist extremist group in North East rejecting Western education and secular state.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">🇷🇺 Russia (2011/2012 & Navalny Protests)</h4>
                <p className="text-sm text-slate-700 mt-2">Protests against election fraud and corruption. Met with arrests and restrictive laws on assembly.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">🇬🇧 United Kingdom (Brexit)</h4>
                <p className="text-sm text-slate-700 mt-2">Social/political movement transcending party lines that pressured government to hold referendum on leaving EU.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-4-6",
      title: "Topic 4.6: Pluralist and Corporatist Interests",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🤝 Pluralism (Democratic Ideal)</h3>
            <div className="bg-green-50 p-4 rounded-lg border border-green-200 mb-4">
              <h4 className="font-bold text-green-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">A system where many independent interest groups compete for power and influence. Government is neutral and open to pressure from any group.</p>
            </div>
            <div className="space-y-2">
              <p className="text-sm text-slate-700"><strong>🇬🇧 UK:</strong> Trade Union Congress (TUC) vs. Confederation of British Industry (CBI) competing to influence parliament.</p>
              <p className="text-sm text-slate-700"><strong>🇳🇬 Nigeria:</strong> Labor unions (NLC) frequently strike to protest fuel prices, acting independently of state.</p>
              <p className="text-sm text-slate-700"><strong>🇲🇽 Mexico:</strong> Since 2000, moved from corporatism toward pluralism, with independent groups emerging.</p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">🏛️ Corporatism (Authoritarian/Hybrid Control)</h3>
            <div className="bg-red-50 p-4 rounded-lg border border-red-200 mb-4">
              <h4 className="font-bold text-red-800">Definition</h4>
              <p className="text-sm text-slate-700 mt-2">State controls which interest groups are allowed to exist. State grants monopoly to specific organizations to represent a sector, and these groups negotiate directly with state.</p>
            </div>
            <div className="space-y-3">
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">State Corporatism</h4>
                <p className="text-sm text-slate-700 mt-2">State creates and controls the groups.</p>
                <p className="text-sm text-slate-700"><strong>🇨🇳 China:</strong> "Mass Organizations." All unions and NGOs must register with government and be supervised by CCP (Transmission Belts).</p>
                <p className="text-sm text-slate-700"><strong>🇷🇺 Russia:</strong> Putin created "Public Chamber" to manage civil society. NGOs receiving foreign money labeled "Foreign Agents."</p>
                <p className="text-sm text-slate-700"><strong>🇲🇽 Mexico (Historical):</strong> Under PRI, peasants and workers forced into state-controlled unions (CTM), ensuring loyalty to party.</p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">📊 Comparison of Access</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">Pluralist Systems</h4>
                <p className="text-sm text-slate-700 mt-2">Rely on lobbying, fundraising, and public opinion to influence policy.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">Corporatist Systems</h4>
                <p className="text-sm text-slate-700 mt-2">Rely on relationship between group leaders and state. Groups act as mechanisms for state to control population, not just for population to influence state.</p>
              </div>
            </div>
          </section>
        </div>
      )
    }
  ]
};

export default governmentUnit4Notes;